import type { OffersEmbedResolvedData } from "../schemas/upsell-constructs";
import { computeUpsellPrice } from "./upsell-pricing";

// Structural shape of the styles prop forwarded to `@codway-dev/ui`.
// Mirrored here (rather than imported) so `@codway-dev/shared` stays
// free of UI-package dependencies — both admin and widget spread the
// result into the bundle field components and TypeScript matches
// structurally.
export interface BundleEmbedStyles {
  backgroundColor?: string;
  borderStyle?: "solid" | "dashed" | "dotted" | "none";
  borderWidth?: number;
  borderColor?: string;
  activeBorderColor?: string;
  borderRadius?: number;
}

export interface BundleItemDisplayProps {
  id: string;
  variantId: string;
  productTitle: string;
  imageUrl?: string;
  quantity: number;
  // Per-item catalog price, when the merchant configured one on the
  // item's display_meta. Useful for showing "$X each" next to the qty
  // chip. Omitted when the merchant left it blank.
  priceLabel?: string;
}

export interface BundleOptionDisplayProps {
  id: string;
  variantId: string;
  productTitle: string;
  imageUrl?: string;
}

export interface BundleGroupDisplayProps {
  id: string;
  name: string;
  pickCount: number;
  options: BundleOptionDisplayProps[];
}

interface BundleDisplayBase {
  title?: string;
  description?: string;
  imageUrl?: string;
  totalPriceLabel: string;
  // Strikethrough anchor — present only when there's a real discount
  // (bundled total < anchor). Anchor is `compare_at_price` when set,
  // otherwise falls back to `base_price`.
  compareAtLabel?: string;
  savingsLabel?: string;
  styles?: BundleEmbedStyles;
}

export interface FixedBundleDisplayProps extends BundleDisplayBase {
  kind: "fixed";
  items: BundleItemDisplayProps[];
}

export interface ConfigurableBundleDisplayProps extends BundleDisplayBase {
  kind: "configurable";
  groups: BundleGroupDisplayProps[];
}

export type BundleEmbedFieldProps =
  | FixedBundleDisplayProps
  | ConfigurableBundleDisplayProps;

export function mapBundleResolvedDataToFieldProps(
  resolvedData: OffersEmbedResolvedData,
  formatPrice: (amount: number) => string
): BundleEmbedFieldProps | null {
  const dd = resolvedData.display_data;
  if (dd.component_type !== "bundle") return null;

  // `base_price` is the bundle's catalog total (pre-calculated by the
  // admin at save time). The offer's `pricing_logic` is applied on top
  // to derive the effective bundled price.
  const basePrice = dd.base_price;
  const bundledTotal = computeUpsellPrice(
    resolvedData.pricing_logic,
    basePrice
  );

  // Strikethrough anchor: prefer the merchant-configured MSRP
  // (`compare_at_price`) when present, otherwise fall back to
  // `base_price`. We only surface the strikethrough when the bundled
  // total is actually below the anchor — guards malformed data where
  // `compare_at_price` was set below `base_price`. Suppressed entirely
  // when the merchant flipped `hide_compare_at_price` — the savings
  // badge is similarly hidden so the two stay consistent.
  const anchor = dd.compare_at_price ?? basePrice;
  const showAnchor = !dd.hide_compare_at_price && bundledTotal < anchor;
  const compareAtLabel = showAnchor ? formatPrice(anchor) : undefined;
  const savings = anchor - bundledTotal;
  const savingsLabel =
    showAnchor && savings > 0 ? formatPrice(savings) : undefined;

  const looseStyles = dd.styles as Record<string, unknown> | undefined;
  const activeBorderColor =
    typeof looseStyles?.active_border_color === "string"
      ? looseStyles.active_border_color
      : undefined;

  const styles: BundleEmbedStyles | undefined = dd.styles
    ? {
        backgroundColor: dd.styles.background_color,
        borderStyle: dd.styles.border_style,
        borderWidth: dd.styles.border_width,
        borderColor: dd.styles.border_color,
        activeBorderColor,
        borderRadius: dd.styles.border_radius,
      }
    : undefined;

  const base: BundleDisplayBase = {
    title: dd.title_override,
    description: dd.description,
    imageUrl: dd.image_url,
    totalPriceLabel: formatPrice(bundledTotal),
    compareAtLabel,
    savingsLabel,
    styles,
  };

  if (dd.items_definition.bundle_type === "fixed") {
    const items: BundleItemDisplayProps[] = dd.items_definition.items.map(
      (item, index) => {
        const meta = item.display_meta;
        const itemPrice = meta?.price ? parseFloat(meta.price) : undefined;
        return {
          id: `item-${index}`,
          variantId: item.variant_id,
          productTitle: meta?.title ?? "",
          imageUrl: meta?.image_url,
          quantity: item.quantity,
          priceLabel:
            itemPrice !== undefined && Number.isFinite(itemPrice)
              ? formatPrice(itemPrice)
              : undefined,
        };
      }
    );
    return { kind: "fixed", items, ...base };
  }

  const groups: BundleGroupDisplayProps[] = dd.items_definition.groups.map(
    (group) => ({
      id: group.group_id,
      name: group.name,
      pickCount: group.pick_count,
      options: group.options.map((option, index) => ({
        id: `${group.group_id}-option-${index}`,
        variantId: option.variant_id,
        productTitle: option.display_meta?.title ?? "",
        imageUrl: option.display_meta?.image_url,
      })),
    })
  );
  return { kind: "configurable", groups, ...base };
}
